import { useEffect, useMemo, useState } from 'react';
import {
  Text,
  Card,
  Button,
  Input,
  Modal,
  Badge,
  EmptyState,
  Table,
  SlideOver,
  SearchInput,
  useToast,
} from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import {
  useGetPricingDefaultsQuery,
  useUpdatePricingDefaultsMutation,
  useListOrgPricingQuery,
  useGetOrgPricingQuery,
  useUpdateOrgPricingMutation,
  useResetOrgPricingMutation,
  type OrgPricingListItem,
  type PricingDefaults,
} from '../../store/api/staffApi';
import {
  Actions,
  AuditNote,
  DiffBlock,
  EffectiveThreshold,
  Field,
  FieldTip,
  OperatorBadgeRow,
  OrgIdHint,
  OrgNameCell,
  PricingDefaultsGrid,
  PricingDefaultsMeta,
  PricingSlideFields,
  SearchWrap,
  SectionSub,
  SectionTitle,
  SlideActions,
  SlideMetaRow,
  Subtitle,
} from './Staff.styles';
import {
  effectiveIncluded,
  formatJoinedDate,
  formatMoneyPreview,
  formatPricingDiff,
  truncateOrgId,
  type PricingRates,
} from './pricingPreview';

const RATE_FIELDS: Array<{
  key: keyof PricingRates;
  label: string;
  tip: string;
}> = [
  {
    key: 'subscriptionFeeUsd',
    label: 'Subscription fee ($)',
    tip: 'Monthly plan fee; basis for the included threshold.',
  },
  {
    key: 'subscriptionDiscountPct',
    label: 'Subscription discount (%)',
    tip: 'Reduces fee paid. Customers see the net amount only.',
  },
  {
    key: 'includedDiscountPct',
    label: 'Included-tier discount (%)',
    tip: 'Derives included $ threshold: fee ÷ (1 − this %).',
  },
  {
    key: 'usageDiscountPct',
    label: 'Usage-tier discount (%)',
    tip: 'Rates overage above the included threshold.',
  },
  {
    key: 'profitMarginPct',
    label: 'Margin (%)',
    tip: 'Markup from LLM cost to pre-discount customer cost (staff-only).',
  },
  {
    key: 'freeSubscriptionMonths',
    label: 'Free subscription months',
    tip: 'First N calendar months from joined date; subscription $0. Usage overage still bills.',
  },
];

type ConfirmMode = 'defaults' | 'org' | 'reset';

function ratesFromDefaults(d: PricingDefaults): PricingRates {
  return {
    subscriptionFeeUsd: d.subscriptionFeeUsd,
    subscriptionDiscountPct: d.subscriptionDiscountPct,
    includedDiscountPct: d.includedDiscountPct,
    usageDiscountPct: d.usageDiscountPct,
    profitMarginPct: d.profitMarginPct,
    freeSubscriptionMonths: d.freeSubscriptionMonths ?? 2,
  };
}

function errMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: { message?: string; errorMessage?: string } })
      .data;
    if (data?.message) return data.message;
    if (data?.errorMessage) return data.errorMessage;
  }
  if (err instanceof Error) return err.message;
  return 'Pricing update failed. Try again.';
}

export function PricingPanel() {
  const toast = useToast();
  const { data: defaults, isLoading } = useGetPricingDefaultsQuery();
  const [updateDefaults, { isLoading: savingDefaults }] =
    useUpdatePricingDefaultsMutation();
  const [q, setQ] = useState('');
  const {
    data: orgList,
    isFetching: orgsFetching,
    refetch: refetchOrgs,
  } = useListOrgPricingQuery({ q });
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const { data: orgDetail } = useGetOrgPricingQuery(selectedOrgId!, {
    skip: !selectedOrgId,
  });
  const [updateOrg, { isLoading: savingOrg }] = useUpdateOrgPricingMutation();
  const [resetOrg, { isLoading: resettingOrg }] = useResetOrgPricingMutation();

  const [draft, setDraft] = useState<PricingRates>({
    subscriptionFeeUsd: 20,
    subscriptionDiscountPct: 0,
    includedDiscountPct: 50,
    usageDiscountPct: 60,
    profitMarginPct: 30,
    freeSubscriptionMonths: 2,
  });
  const [orgDraft, setOrgDraft] = useState<PricingRates>({
    subscriptionFeeUsd: 20,
    subscriptionDiscountPct: 0,
    includedDiscountPct: 50,
    usageDiscountPct: 60,
    profitMarginPct: 30,
    freeSubscriptionMonths: 2,
  });
  const [orgSnapshot, setOrgSnapshot] = useState<PricingRates | null>(null);
  const [defaultsSnapshot, setDefaultsSnapshot] = useState<PricingRates | null>(
    null,
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmNote, setConfirmNote] = useState('');
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>('defaults');

  const saving = savingDefaults || savingOrg || resettingOrg;

  useEffect(() => {
    if (defaults) {
      setDraft(ratesFromDefaults(defaults));
    }
  }, [defaults]);

  useEffect(() => {
    if (!orgDetail?.resolved || !selectedOrgId) return;
    const r = orgDetail.resolved;
    const rates: PricingRates = {
      subscriptionFeeUsd: r.subscriptionFeeUsd,
      subscriptionDiscountPct: r.subscriptionDiscountPct,
      includedDiscountPct: r.includedDiscountPct,
      usageDiscountPct: r.usageDiscountPct,
      profitMarginPct: r.profitMarginPct,
      freeSubscriptionMonths: r.freeSubscriptionMonths ?? 2,
    };
    setOrgDraft(rates);
    setOrgSnapshot(rates);
  }, [
    selectedOrgId,
    orgDetail?.orgId,
    orgDetail?.resolved?.subscriptionFeeUsd,
    orgDetail?.resolved?.subscriptionDiscountPct,
    orgDetail?.resolved?.includedDiscountPct,
    orgDetail?.resolved?.usageDiscountPct,
    orgDetail?.resolved?.profitMarginPct,
    orgDetail?.resolved?.freeSubscriptionMonths,
  ]);

  const includedPreview = useMemo(
    () =>
      effectiveIncluded(draft.subscriptionFeeUsd, draft.includedDiscountPct),
    [draft],
  );

  const orgIncludedPreview = useMemo(
    () =>
      effectiveIncluded(
        orgDraft.subscriptionFeeUsd,
        orgDraft.includedDiscountPct,
      ),
    [orgDraft],
  );

  const confirmDiff = useMemo(() => {
    if (confirmMode === 'defaults') {
      const before = defaultsSnapshot || draft;
      return formatPricingDiff(before, draft);
    }
    if (confirmMode === 'org' && orgSnapshot) {
      return formatPricingDiff(orgSnapshot, orgDraft);
    }
    if (confirmMode === 'reset' && orgSnapshot && defaults) {
      return formatPricingDiff(orgSnapshot, ratesFromDefaults(defaults));
    }
    return [];
  }, [
    confirmMode,
    defaultsSnapshot,
    draft,
    orgSnapshot,
    orgDraft,
    defaults,
  ]);

  const openDefaultsConfirm = () => {
    setDefaultsSnapshot(defaults ? ratesFromDefaults(defaults) : { ...draft });
    setConfirmMode('defaults');
    setConfirmNote('');
    setConfirmOpen(true);
  };

  const openOrgConfirm = () => {
    setConfirmMode('org');
    setConfirmNote('');
    setConfirmOpen(true);
  };

  const openResetConfirm = () => {
    setConfirmMode('reset');
    setConfirmNote('');
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (saving) return;
    setConfirmOpen(false);
    setConfirmNote('');
  };

  const applyConfirm = async () => {
    if (!confirmNote.trim() || saving) return;
    try {
      if (confirmMode === 'defaults') {
        await updateDefaults({ ...draft, note: confirmNote.trim() }).unwrap();
        toast.show({
          message: 'Platform defaults saved (audited).',
          type: 'success',
        });
      } else if (confirmMode === 'org' && selectedOrgId) {
        await updateOrg({
          orgId: selectedOrgId,
          subscriptionFeeUsd: orgDraft.subscriptionFeeUsd,
          subscriptionDiscountPct: orgDraft.subscriptionDiscountPct,
          includedDiscountPct: orgDraft.includedDiscountPct,
          usageDiscountPct: orgDraft.usageDiscountPct,
          profitMarginPct: orgDraft.profitMarginPct,
          freeSubscriptionMonths: orgDraft.freeSubscriptionMonths,
          note: confirmNote.trim(),
        }).unwrap();
        refetchOrgs();
        toast.show({
          message: 'Organisation pricing applied (audited).',
          type: 'success',
        });
        setSelectedOrgId(null);
      } else if (confirmMode === 'reset' && selectedOrgId) {
        await resetOrg({
          orgId: selectedOrgId,
          note: confirmNote.trim(),
        }).unwrap();
        refetchOrgs();
        toast.show({
          message: 'Organisation reset to platform defaults (audited).',
          type: 'success',
        });
        setSelectedOrgId(null);
      }
      setConfirmOpen(false);
      setConfirmNote('');
    } catch (err) {
      toast.show({ message: errMessage(err), type: 'error' });
    }
  };

  const orgColumns: Column<OrgPricingListItem>[] = useMemo(
    () => [
      {
        key: 'orgName',
        header: 'Organisation',
        render: (_val, row) => (
          <OrgNameCell>
            <span style={{ fontWeight: 500 }}>{row.orgName}</span>
            <OrgIdHint>{truncateOrgId(row.orgId)}</OrgIdHint>
          </OrgNameCell>
        ),
      },
      {
        key: 'joinedAt',
        header: 'Joined',
        render: (_val, row) => (
          <span style={{ fontFamily: 'ui-monospace, monospace' }}>
            {formatJoinedDate(row.joinedAt)}
          </span>
        ),
      },
      {
        key: 'subscriptionFeeUsd',
        header: 'Fee',
        render: (_val, row) => (
          <span style={{ fontFamily: 'ui-monospace, monospace' }}>
            {formatMoneyPreview(row.subscriptionFeeUsd)}
          </span>
        ),
      },
      {
        key: 'includedThresholdUsd',
        header: 'Effective included',
        render: (_val, row) => (
          <span style={{ fontFamily: 'ui-monospace, monospace' }}>
            {formatMoneyPreview(row.includedThresholdUsd)}
          </span>
        ),
      },
      {
        key: 'effectiveSubscriptionChargedUsd',
        header: 'Effective charged',
        render: (_val, row) => (
          <span style={{ fontFamily: 'ui-monospace, monospace' }}>
            {formatMoneyPreview(row.effectiveSubscriptionChargedUsd)}
            {row.inFreePeriod ? (
              <>
                {' '}
                <Badge variant="success" size="sm">
                  Free
                </Badge>
              </>
            ) : null}
          </span>
        ),
      },
      {
        key: 'hasOverride',
        header: 'Status',
        render: (_val, row) =>
          row.hasOverride ? (
            <Badge variant="info" size="sm">
              Override
            </Badge>
          ) : (
            <Badge variant="neutral" size="sm">
              Default
            </Badge>
          ),
      },
      {
        key: 'edit',
        header: '',
        render: (_val, row) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelectedOrgId(row.orgId)}
          >
            Edit
          </Button>
        ),
      },
    ],
    [],
  );

  const confirmTitle =
    confirmMode === 'defaults'
      ? 'Confirm platform defaults'
      : confirmMode === 'reset'
        ? 'Confirm reset to defaults'
        : 'Confirm pricing change';

  const confirmCta =
    confirmMode === 'defaults'
      ? 'Save defaults'
      : confirmMode === 'reset'
        ? 'Use platform defaults'
        : 'Apply pricing';

  const auditCopy =
    confirmMode === 'defaults'
      ? 'This change is audited. New organisations inherit these defaults. Existing org overrides are unchanged.'
      : confirmMode === 'reset'
        ? 'This change is audited. Clearing the org override restores platform defaults for this organisation.'
        : 'This change is audited. Customers see subscription amount, included threshold, and usage charges on Payments — not rates or LLM cost.';

  if (isLoading) return <Text variant="body2">Loading pricing…</Text>;

  return (
    <div>
      <OperatorBadgeRow>
        <Badge variant="warning">Operator only</Badge>
        <span>
          Platform defaults and per-organisation overrides for subscription and
          two-tier usage. Customers see amounts and included thresholds on
          Payments — not discount %, margin %, or LLM cost.
        </span>
      </OperatorBadgeRow>

      <Card style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
        <SectionTitle>Platform defaults</SectionTitle>
        <PricingDefaultsGrid>
          {RATE_FIELDS.map(({ key, label, tip }) => (
            <Field key={key}>
              {label}
              <Input
                type="number"
                value={String(draft[key])}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    [key]: Number(e.target.value),
                  }))
                }
              />
              <FieldTip>{tip}</FieldTip>
            </Field>
          ))}
        </PricingDefaultsGrid>
        <PricingDefaultsMeta>
          Sample effective included threshold:{' '}
          <strong style={{ fontFamily: 'ui-monospace, monospace' }}>
            {formatMoneyPreview(includedPreview)}
          </strong>
          <span> · fee ÷ (1 − included discount)</span>
        </PricingDefaultsMeta>
        <Button
          size="sm"
          disabled={savingDefaults}
          onClick={openDefaultsConfirm}
        >
          Save platform defaults
        </Button>
      </Card>

      <SectionTitle>Organisations</SectionTitle>
      <SearchWrap>
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search organisation…"
          debounceMs={300}
        />
      </SearchWrap>

      {!orgsFetching && (orgList?.items || []).length === 0 ? (
        <EmptyState
          title={q ? 'No organisations match' : 'No organisations'}
          description={
            q
              ? 'Try a different name or organisation id.'
              : 'Organisations will appear here once created.'
          }
        />
      ) : (
        <Table
          columns={orgColumns}
          data={orgList?.items || []}
          rowKey="orgId"
          loading={orgsFetching}
          emptyMessage="No organisations"
        />
      )}

      <SlideOver
        isOpen={!!selectedOrgId}
        onClose={() => setSelectedOrgId(null)}
        title={orgDetail?.orgName || 'Organisation pricing'}
        width="md"
      >
        <SectionSub>
          Override platform defaults for this organisation. Reset clears the
          override and restores globals.
        </SectionSub>
        <SlideMetaRow>
          <span>
            Joined{' '}
            <strong style={{ fontFamily: 'ui-monospace, monospace' }}>
              {formatJoinedDate(orgDetail?.joinedAt)}
            </strong>
          </span>
          {orgDetail?.inFreePeriod ? (
            <Badge variant="success" size="sm">
              In free subscription period
            </Badge>
          ) : null}
        </SlideMetaRow>
        {orgDetail?.orgId && (
          <Subtitle style={{ marginBottom: '0.75rem' }}>
            <OrgIdHint>{orgDetail.orgId}</OrgIdHint>
            {orgDetail.resolved?.source
              ? ` · source ${orgDetail.resolved.source}`
              : null}
          </Subtitle>
        )}
        <PricingSlideFields>
          {RATE_FIELDS.map(({ key, label, tip }) => (
            <Field key={key}>
              {label}
              <Input
                type="number"
                value={String(orgDraft[key])}
                onChange={(e) =>
                  setOrgDraft((prev) => ({
                    ...prev,
                    [key]: Number(e.target.value),
                  }))
                }
              />
              <FieldTip>{tip}</FieldTip>
            </Field>
          ))}
        </PricingSlideFields>
        <EffectiveThreshold>
          Effective included threshold:{' '}
          <strong style={{ fontFamily: 'ui-monospace, monospace' }}>
            {formatMoneyPreview(orgIncludedPreview)}
          </strong>
          <div style={{ marginTop: '0.35rem', fontSize: '0.8125rem' }}>
            Effective charged this month:{' '}
            <strong style={{ fontFamily: 'ui-monospace, monospace' }}>
              {formatMoneyPreview(
                orgDetail?.effectiveSubscriptionChargedUsd ??
                  (orgDetail?.inFreePeriod
                    ? 0
                    : orgDraft.subscriptionFeeUsd *
                      (1 - orgDraft.subscriptionDiscountPct / 100)),
              )}
            </strong>
          </div>
        </EffectiveThreshold>
        <SlideActions>
          <Button onClick={openOrgConfirm} disabled={saving}>
            Apply overrides
          </Button>
          <Button
            variant="outline"
            onClick={openResetConfirm}
            disabled={saving}
          >
            Use platform defaults
          </Button>
        </SlideActions>
      </SlideOver>

      <Modal isOpen={confirmOpen} onClose={closeConfirm} title={confirmTitle}>
        {confirmMode === 'org' && (
          <Text variant="body2">
            Update pricing for{' '}
            <strong>{orgDetail?.orgName || 'this organisation'}</strong>?
          </Text>
        )}
        {confirmMode === 'defaults' && (
          <Text variant="body2">Save new platform pricing defaults?</Text>
        )}
        {confirmMode === 'reset' && (
          <Text variant="body2">
            Clear overrides for{' '}
            <strong>{orgDetail?.orgName || 'this organisation'}</strong> and use
            platform defaults?
          </Text>
        )}
        <DiffBlock>
          {confirmDiff.map((line) => (
            <div key={line.label}>
              {line.label}: <strong>{line.before}</strong> →{' '}
              <strong>{line.after}</strong>
              {line.note ? ` (${line.note})` : null}
            </div>
          ))}
        </DiffBlock>
        <AuditNote>{auditCopy}</AuditNote>
        <Field>
          Audit note (required)
          <Input
            value={confirmNote}
            onChange={(e) => setConfirmNote(e.target.value)}
            placeholder="Reason for change"
            disabled={saving}
          />
        </Field>
        <Actions>
          <Button
            disabled={!confirmNote.trim() || saving}
            onClick={() => void applyConfirm()}
          >
            {confirmCta}
          </Button>
          <Button variant="ghost" onClick={closeConfirm} disabled={saving}>
            Cancel
          </Button>
        </Actions>
      </Modal>
    </div>
  );
}

export default PricingPanel;
